import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import FlowEngineGlobalGate from "../specialist-flow-engine/FlowEngineGlobalGate";
import { startLoadingCatalogModules } from "../../app/actions/catalogModuleActions";
import {
  startDeleteFlowCoverageGroup,
  startLoadingFlowCoverageGroups,
  startSaveFlowCoverageGroup
} from "../../app/actions/flowCoverageActions";
import { getCatalogModuleOptionLabel, getModuleDisplayLabelById } from "../../app/helpers/flowEngineConfigHelper";
import { normalizeSpecialistRoleKey } from "../../app/helpers/activeSpecialistRoleHelper";
import { getModuleWorkAxis, isSegmentingWorkAxis } from "../../app/helpers/flowCoverageAxisHelper";

const ROLE_OPTIONS = [
  { id: "feeder", labelKey: "admin.flowModule.diagram.role.feeder", fallback: "Alimentador" },
  { id: "pre-manager", labelKey: "admin.flowModule.diagram.role.pre-manager", fallback: "Pre-Gestor" },
  { id: "manager", labelKey: "admin.flowModule.diagram.role.manager", fallback: "Gestor" },
  { id: "validator", labelKey: "admin.flowModule.diagram.role.validator", fallback: "Validador" },
  { id: "editor", labelKey: "admin.flowModule.diagram.role.editor", fallback: "Editor" }
];

const ROLE_ID_TO_KEY = {
  3: "feeder",
  4: "pre-manager",
  5: "manager",
  6: "validator",
  7: "editor"
};

const emptyForm = {
  name: "",
  code: "",
  catalogmodule_id: "",
  role: "manager",
  area_keys: []
};

const PROBLEM_LETTER_TO_NUMBER = {
  A: "1", B: "2", C: "3", D: "4", E: "5", F: "6", G: "7", H: "8", I: "9", J: "10",
  K: "11", L: "12", M: "13", N: "14", O: "15", P: "16", Q: "17", R: "18", S: "19", T: "20", U: "21"
};

const normalizeProblemKey = (key) => PROBLEM_LETTER_TO_NUMBER[String(key || "").toUpperCase()] || String(key || "");

const normalizeGroupAreaKeys = (keys, axis) => {
  const list = Array.isArray(keys) ? keys : [];
  if (axis !== "problem") {
    return [...list];
  }
  return list.map(normalizeProblemKey);
};

const AdminFlowCoverageGroupsScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { catalogModules = [] } = useSelector((state) => state.catalogModule);
  const { loading, groups, availableKeys } = useSelector((state) => state.flowCoverage);
  const [moduleId, setModuleId] = useState("");
  const [roleKey, setRoleKey] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const selectedAxis = getModuleWorkAxis(moduleId);
  const canEditBlocks = isSegmentingWorkAxis(selectedAxis);
  const fallbackKeys = {
    problem: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21"],
    organism: ["INT", "NAC", "OTH"],
    operation_size: ["MED-GRAN", "PEQ-MIC", "OTH"],
    topic: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27"],
    ciiu_l1: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U"]
  };
  const keys = availableKeys.length ? availableKeys : (fallbackKeys[selectedAxis] || []);

  useEffect(() => {
    dispatch(startLoadingCatalogModules());
  }, [dispatch]);

  const filters = useMemo(() => {
    const next = {};
    if (moduleId) {
      next.catalogmodule_id = moduleId;
    }
    if (roleKey) {
      next.role = roleKey;
    }
    return next;
  }, [moduleId, roleKey]);

  useEffect(() => {
    dispatch(startLoadingFlowCoverageGroups(filters));
  }, [dispatch, filters]);

  const reload = () => {
    dispatch(startLoadingFlowCoverageGroups(filters));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      catalogmodule_id: moduleId,
      role: roleKey || "manager"
    });
  };

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      catalogmodule_id: moduleId || prev.catalogmodule_id,
      role: roleKey || prev.role,
      area_keys: []
    }));
    setEditingId(null);
  }, [moduleId, roleKey]);

  const toggleKey = (key, current, setter) => {
    const exists = current.includes(key);
    setter(exists ? current.filter((item) => item !== key) : [...current, key]);
  };

  const loadGroup = (group) => {
    setEditingId(group.id);
    setForm({
      name: group.name || "",
      code: group.code || "",
      catalogmodule_id: String(group.catalogmodule_id),
      role: ROLE_ID_TO_KEY[Number(group.catalogrole_id)] || "manager",
      area_keys: normalizeGroupAreaKeys(group.area_keys, getModuleWorkAxis(group.catalogmodule_id))
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.catalogmodule_id || !form.name || !form.role) {
      Swal.fire(t("titles.error"), t("admin.flowCoverageGroups.validation"), "error");
      return;
    }
    const saved = await dispatch(startSaveFlowCoverageGroup({
      name: form.name,
      code: form.code,
      catalogmodule_id: Number(form.catalogmodule_id),
      role: form.role,
      area_keys: form.area_keys
    }, editingId));
    if (saved) {
      resetForm();
      reload();
    }
  };

  const remove = async (group) => {
    const confirmed = await Swal.fire({
      title: t("admin.flowCoverageGroups.deleteTitle"),
      text: t("admin.flowCoverageGroups.deleteText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("buttons.delete"),
      cancelButtonText: t("buttons.cancel")
    });
    if (!confirmed.isConfirmed) {
      return;
    }
    const ok = await dispatch(startDeleteFlowCoverageGroup(group.id));
    if (ok) {
      if (Number(editingId) === Number(group.id)) {
        resetForm();
      }
      reload();
    }
  };

  const axisHelp = t(`admin.flowCoverageGroups.axis.${{
    problem: "problem",
    ciiu_l1: "ciiu",
    organism: "organism",
    operation_size: "operationSize",
    topic: "topic"
  }[selectedAxis] || "none"}`);

  return (
    <FlowEngineGlobalGate>
      <div className="container py-4">
        <h2>{t("admin.flowCoverageGroups.title")}</h2>
        <p className="text-muted">{t("admin.flowCoverageGroups.subtitle")}</p>
        <p className="small">
          {t("admin.flowCoverageGroups.peopleLinkHint")}
          {" "}
          <Link to="/admin-flow-module-config">{t("admin.flowCoverageGroups.peopleLink")}</Link>
        </p>

        <div className="row g-2 mb-3">
          <div className="col-md-4">
            <label className="form-label">{t("admin.flowCoverageGroups.module")}</label>
            <select className="form-select" value={moduleId} onChange={(event) => setModuleId(event.target.value)}>
              <option value="">{t("admin.flowCoverageGroups.allModules")}</option>
              {(catalogModules || []).filter((item) => !item.deleted).map((item) => (
                <option key={item.id} value={item.id}>{getCatalogModuleOptionLabel(item, t)}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">{t("admin.flowCoverageGroups.role")}</label>
            <select className="form-select" value={roleKey} onChange={(event) => setRoleKey(event.target.value)}>
              <option value="">{t("admin.flowCoverageGroups.allRoles")}</option>
              {ROLE_OPTIONS.map((item) => (
                <option key={item.id} value={item.id}>{t(item.labelKey, item.fallback)}</option>
              ))}
            </select>
          </div>
        </div>

        {moduleId ? <p className="text-muted small">{axisHelp}</p> : null}
        {loading && <p>{t("labels.loading", "Cargando...")}</p>}

        {(canEditBlocks || editingId) && (
          <form className="card card-body mb-4" onSubmit={submit}>
            <h5 className="mb-3">
              {editingId ? t("admin.flowCoverageGroups.editPack") : t("admin.flowCoverageGroups.newPack")}
            </h5>
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <input
                  className="form-control"
                  placeholder={t("admin.flowCoverageGroups.name")}
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
              <div className="col-md-3">
                <input
                  className="form-control"
                  placeholder={t("admin.flowCoverageGroups.code")}
                  value={form.code}
                  onChange={(event) => setForm({ ...form, code: event.target.value })}
                />
              </div>
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={form.role}
                  onChange={(event) => setForm({ ...form, role: event.target.value })}
                  disabled={!!editingId}
                >
                  {ROLE_OPTIONS.map((item) => (
                    <option key={item.id} value={item.id}>{t(item.labelKey, item.fallback)}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <button type="submit" className="btn btn-primary w-100">
                  {editingId ? t("buttons.update", "Actualizar") : t("buttons.add")}
                </button>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {keys.map((key) => (
                <label key={key} className="form-check-label border rounded px-2">
                  <input
                    type="checkbox"
                    className="form-check-input me-1"
                    checked={form.area_keys.includes(key)}
                    onChange={() => toggleKey(key, form.area_keys, (next) => setForm({ ...form, area_keys: next }))}
                  />
                  {key}
                </label>
              ))}
            </div>
            {editingId && (
              <button type="button" className="btn btn-link p-0" onClick={resetForm}>
                {t("buttons.cancel")}
              </button>
            )}
          </form>
        )}

        {(groups || []).map((group) => {
          const areas = normalizeGroupAreaKeys(group.area_keys, getModuleWorkAxis(group.catalogmodule_id)).join(", ");
          return (
            <div className="card mb-3" key={group.id}>
              <div className="card-header d-flex flex-wrap justify-content-between gap-2">
                <strong>{group.name || group.code}</strong>
                <span className="small">
                  {getCatalogModuleOptionLabel(group.CatalogModule_To_FlowCoverageGroup, t)
                    || getModuleDisplayLabelById(group.catalogmodule_id, t, String(group.catalogmodule_id))}
                  {" · "}
                  {(() => {
                    const roleName = group.CatalogRole_To_FlowCoverageGroup?.name;
                    const key = ROLE_ID_TO_KEY[Number(group.catalogrole_id)]
                      || normalizeSpecialistRoleKey({ name: roleName });
                    const option = ROLE_OPTIONS.find((item) => item.id === key);
                    return option ? t(option.labelKey, option.fallback) : (roleName || group.catalogrole_id);
                  })()}
                </span>
              </div>
              <div className="card-body">
                <p className="mb-2 small text-muted">{t("admin.flowCoverageGroups.items")}: {areas || "—"}</p>
                <button type="button" className="btn btn-link p-0 me-3" onClick={() => loadGroup(group)}>
                  {t("buttons.edit", "Editar")}
                </button>
                <button type="button" className="btn btn-link text-danger p-0" onClick={() => remove(group)}>
                  {t("buttons.delete")}
                </button>
              </div>
            </div>
          );
        })}

        {!loading && !(groups || []).length && (
          <p className="text-muted">{t("admin.flowCoverageGroups.empty")}</p>
        )}
      </div>
    </FlowEngineGlobalGate>
  );
};

export default AdminFlowCoverageGroupsScreen;
